import { useEffect, useRef, useState } from "react";
import { Button, Input, Label } from "@/design-system/components/ui";
import { Modal } from "./Modal";

/**
 * Phoenix Capital modals that replace the browser-native
 * window.prompt / window.confirm / window.alert dialogs with components
 * that match the design system (gold-on-black aesthetic, Montserrat, etc.).
 *
 * Everything here is a thin wrapper around the shared {@link Modal}
 * component; they exist so call sites can open them imperatively via
 * {@link useDialogs} without having to manage their own visibility state.
 */

// ---------- ConfirmModal ---------------------------------------------

export interface ConfirmModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmModal({
  open, title, message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm, onCancel,
}: ConfirmModalProps) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={destructive ? "destructive" : "primary"} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className="text-small text-gray-dark">{message}</div>
    </Modal>
  );
}

// ---------- InputModal ------------------------------------------------

export interface InputModalProps {
  open: boolean;
  title: string;
  label: string;
  placeholder?: string;
  defaultValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Returns an error string to display, or null to accept. */
  validate?: (value: string) => string | null;
  onSubmit: (value: string) => void;
  onCancel: () => void;
}

export function InputModal({
  open, title, label, placeholder, defaultValue = "",
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  validate,
  onSubmit, onCancel,
}: InputModalProps) {
  const [value, setValue] = useState(defaultValue);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      setError(null);
      // Focus the input after the modal renders
      setTimeout(() => inputRef.current?.focus(), 10);
    }
  }, [open, defaultValue]);

  const submit = () => {
    const trimmed = value.trim();
    const errMsg = validate ? validate(trimmed) : (trimmed ? null : `${label} is required.`);
    if (errMsg) {
      setError(errMsg);
      return;
    }
    onSubmit(trimmed);
  };

  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      size="sm"
      dismissOnBackdropClick={false}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button onClick={submit} disabled={!value.trim()}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="grid gap-space-2">
        <Label required>{label}</Label>
        <Input
          ref={inputRef}
          value={value}
          onChange={(e) => { setValue(e.target.value); if (error) setError(null); }}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); submit(); }
          }}
          placeholder={placeholder}
        />
        {error && <div className="text-caption text-status-error" role="alert">{error}</div>}
      </div>
    </Modal>
  );
}

// ---------- AlertModal ------------------------------------------------

export interface AlertModalProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  tone?: "info" | "success" | "error";
  okLabel?: string;
  onClose: () => void;
}

export function AlertModal({
  open, title, message, tone = "info", okLabel = "OK", onClose,
}: AlertModalProps) {
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size="sm"
      footer={<Button onClick={onClose}>{okLabel}</Button>}
    >
      <div className={"text-small " +
        (tone === "error" ? "text-status-error" :
         tone === "success" ? "text-status-success" :
         "text-gray-dark")}>
        {message}
      </div>
    </Modal>
  );
}
